'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { BuilderComponent, builder } from '@builder.io/react';
import getVigLinkUrl from '@/utils/getVigLinkUrl';
import MessagesSlider from '@/components/@generalComponents/MessagesSlider';
import MessageCard from '@/components/Home/MessageCard';
import FollowButton from '@/components/@generalComponents/FollowButton';
import PaywallCard from '@/components/@generalComponents/PaywallCard';
import LeftSidebar from '@/components/layout/LeftSidebar';
import SortControl from '@/components/@generalComponents/SortControl';
import TagFilter, {
	extractUniqueTags,
	filterByTag,
} from '@/components/@generalComponents/TagFilter';
import {
	withEffectiveTags,
	FILTER_TAG_LABELS,
	FILTER_TAG_ORDER,
} from '@/utils/messageFilters';
import SecondaryFooter from '@/components/layout/SecondaryFooter';
import { applyPaywall, shouldShowPaywall } from '@/utils/paywall';
import { sortMessagesByDate } from '@/utils/sort';
import { UserAuth } from '@/app/context/AuthContext';
import { getAuthHeaders } from '@/utils/authHeader';
import { fetchAvailableTags } from '@/utils/tagFilterApi';
import { GCS_BUCKET_URI } from '@/exports';
import { useParams } from 'next/navigation';
import { useMediaQuery, useTheme } from '@mui/material';
import styles from './SingleBrandPage.module.css';

builder.init('0de1f0bc4e504a15a1cd1b48dbd897e2');

// The backend caps page size at 50 regardless of what's requested, so the
// background full-set fetch (used only as a client-side fallback for tag
// filtering, see `loadFullBrandMessageSet`) asks for the max allowed size to
// minimize the number of requests.
const FULL_SET_PAGE_SIZE = 50;
// Safety cap on how many pages the full-set fetch will loop through
// (20 pages * 50 = ~1000 messages). Protects against an unbounded loop if a
// future backend response never reports `nextPage: null`. If the cap is hit,
// we simply stop and work with whatever was collected so far - chips and
// filtering reflect the (very large) partial set, and the UI tells the
// visitor it's partial (see `fullSetTruncated`) rather than the page hanging
// or looping forever, or silently pretending the partial set is complete.
const FULL_SET_MAX_PAGES = 20;
// How many messages the capped fetch actually covers. Derived rather than
// written out, so the note shown to visitors when the cap is hit can never
// drift out of sync with the two constants above.
const FULL_SET_MAX_MESSAGES = FULL_SET_PAGE_SIZE * FULL_SET_MAX_PAGES;

// Module-level cache of each brand's full message set, keyed by brandId.
// `/api/messages/available-tags` 404s on every request today, so every
// brand-page visit falls back to fetching this brand's whole message
// history client-side purely to build the tag chip list. Without a cache
// that fallback would re-run - up to `FULL_SET_MAX_PAGES` full-payload
// requests - on every single visit, including navigating away and back in
// the same session. This cache stores the in-flight PROMISE (not just the
// eventual array), so two mounts for the same brand close together (e.g. a
// fast back/forward navigation, or React re-mounting in dev) share one
// fetch instead of issuing it twice, and a later visit to an already-cached
// brand in the same session reuses the result instead of re-fetching. It is
// in-memory only (cleared on a full page reload) - the first-ever visit to
// a brand in a session still pays the full cost, which is unavoidable
// without backend support and is an accepted trade-off; this cache only
// stops that cost from being paid repeatedly.
const fullBrandMessageSetCache = new Map();

// Loops `GET /api/brands/messages/{brandId}` from page 0 until the server
// reports no next page, or the safety cap (`FULL_SET_MAX_PAGES`) is hit - in
// which case it returns `truncated: true` alongside whatever was collected,
// so callers can be truthful about an incomplete set instead of treating it
// as the brand's whole history.
const loadFullBrandMessageSet = async (brandId) => {
	const authHeaders = await getAuthHeaders();
	const collected = [];
	let nextPage = 0;
	let pagesFetched = 0;
	let truncated = false;
	while (nextPage != null) {
		if (pagesFetched >= FULL_SET_MAX_PAGES) {
			truncated = true;
			break;
		}
		const response = await fetch(
			`https://api.tinytexts.co/api/brands/messages/${brandId}?page=${nextPage}&size=${FULL_SET_PAGE_SIZE}`,
			{ headers: authHeaders }
		);
		if (!response.ok) {
			throw new Error(`Error: ${response.status} - ${response.statusText}`);
		}
		const data = await response.json();
		collected.push(...(data?.messages || []));
		nextPage = data?.pagination?.nextPage ?? null;
		pagesFetched += 1;
	}
	return { messages: collected, truncated };
};

// Returns the cached promise for `brandId` (in-flight or already resolved)
// if one exists, otherwise starts the fetch and caches its promise. A
// failed fetch is removed from the cache rather than cached as if it were a
// success, so a later attempt (e.g. a subsequent visit, or React retrying
// after an error) can retry it instead of being stuck with a permanent
// failure for the rest of the session.
// `authKey` (signed-in user + PRO status) is part of the cache key so a set
// fetched at one tier is never reused for another.
const getFullBrandMessageSet = (brandId, authKey = '') => {
	const cacheKey = `${brandId}|${authKey}`;
	if (!fullBrandMessageSetCache.has(cacheKey)) {
		const promise = loadFullBrandMessageSet(brandId).catch((error) => {
			fullBrandMessageSetCache.delete(cacheKey);
			throw error;
		});
		fullBrandMessageSetCache.set(cacheKey, promise);
	}
	return fullBrandMessageSetCache.get(cacheKey);
};

const transformBrand = (rawBrand) => {
	if (!rawBrand) return null;
	return {
		brandId: rawBrand?.brandId,
		categoryId: rawBrand?.category?.categoryId,
		name: rawBrand?.name,
		phoneNumbers: rawBrand?.phoneNumbers,
		description: rawBrand?.description,
		iconUrl: rawBrand?.iconObjectName
			? `${GCS_BUCKET_URI}/brand-icons/${rawBrand?.iconObjectName}`
			: null,
		websiteLink: rawBrand?.websiteLink,
		messages: rawBrand?.messages,
	};
};

const SingleBrandPage = ({ data, brand: brandProp }) => {
	const params = useParams();
	const theme = useTheme();
	const isMd = useMediaQuery(theme.breakpoints.down('md'));

	const [brand, setBrand] = useState(() => transformBrand(brandProp));
	const [messages, setMessages] = useState(data?.messages || []);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState(data?.pagination || null);
	const [content, setContent] = useState(null);
	const [sortDirection, setSortDirection] = useState('desc');
	const [selectedTag, setSelectedTag] = useState('');
	const { user, isPro } = UserAuth();
	const authKey = `${user?.uid ?? ''}:${isPro ? 'pro' : 'free'}`;
	const authKeyRef = useRef(authKey);
	authKeyRef.current = authKey;

	// Chips normally come from the server's available-tags endpoint, which
	// covers this brand's ENTIRE message set (not just what's loaded on
	// screen). `tagsSource` starts as 'client' so a chip list is available
	// immediately from whatever messages are already loaded, then upgrades to
	// 'server' once the available-tags call resolves. If that call - or a
	// tag-filtered messages fetch below - ever fails (e.g. the backend hasn't
	// been deployed with this support yet), we stay in 'client' mode. In that
	// mode we no longer only look at whatever happens to already be on
	// screen for the CHIP LIST: we also kick off a background fetch (cached
	// at module scope across mounts/navigations, see `getFullBrandMessageSet`)
	// of the brand's message set (`fullBrandMessages`, looped across pages,
	// capped for safety at `FULL_SET_MAX_PAGES` - see `fullSetTruncated` if
	// that cap is hit) so chips cover as much of the brand's history as we
	// fetched, matching what the server path would have given us. That
	// (possibly truncated) set is ALSO substituted in as the filtering source,
	// but only once the visitor has actually selected a tag - the default,
	// no-tag-selected "All" view is untouched by any of this and always
	// renders from `messages`/`pagination` exactly as it did before this
	// feature existed (see `filterSource` below). Until the background fetch
	// resolves (or if it fails), chips fall back further to whatever is
	// currently loaded in `messages` - exactly how this page behaved before
	// any of this tag-filtering work existed. This keeps the page working,
	// and never blocks or breaks the normal message list, even when
	// frontend and backend are a release apart.
	const [tagsSource, setTagsSource] = useState('client');
	const [serverTags, setServerTags] = useState([]);
	const [filteredMessages, setFilteredMessages] = useState([]);
	const [filteredPagination, setFilteredPagination] = useState(null);
	const [filteredLoading, setFilteredLoading] = useState(false);
	// The brand's complete message set, fetched client-side only when the
	// server's available-tags endpoint is unavailable. Kept in its own state
	// (never merged into `messages`) AND only ever read into the rendered
	// list while a tag is actively selected (`isClientFiltering`, enforced at
	// the `filterSource` derivation below) - so it cannot affect the default
	// view's pagination, "Load More", or the paywall's view of how many
	// messages a free user has been shown.
	const [fullBrandMessages, setFullBrandMessages] = useState(null);
	const [fullBrandMessagesLoading, setFullBrandMessagesLoading] = useState(false);
	// True when the full-set fetch hit `FULL_SET_MAX_PAGES` before running out
	// of pages, i.e. `fullBrandMessages` is a (large) partial set - the most
	// recent ~1,000 messages - not the brand's entire history. We still use
	// it (better than loaded-messages-only) but say so in the UI rather than
	// silently presenting it as complete.
	const [fullSetTruncated, setFullSetTruncated] = useState(false);

	// Guards against out-of-order responses when the brand changes quickly.
	const tagsRequestIdRef = useRef(0);
	// Guards against out-of-order responses when the selected tag (or a
	// "Load More" click while filtering) changes/fires quickly in succession.
	const filterRequestIdRef = useRef(0);
	// Guards the background full-message-set fetch the same way: if the brand
	// changes again before it resolves, its (stale) result is discarded.
	const fullSetRequestIdRef = useRef(0);

	const isFiltering = Boolean(selectedTag) && tagsSource === 'server';
	// A tag is selected but the server never confirmed it supports filtering,
	// so filtering happens in memory (over the full set once it's ready, or
	// over loaded messages until then / if it never becomes ready).
	const isClientFiltering = Boolean(selectedTag) && tagsSource === 'client';
	// True once the background full-set fetch has completed for the current
	// brand (independent of whether a tag is currently selected). NOTE this
	// does not guarantee completeness - if `fullSetTruncated` is also true,
	// `fullBrandMessages` is only the brand's most recent ~1,000 messages,
	// not its entire history (see `FULL_SET_MAX_PAGES`). Used to (a) source
	// the tag chip list, which reflects as much of the brand's history as was
	// fetched, and (b) as one of the two conditions (together with
	// `isClientFiltering`) for switching the rendered message list over to
	// the fetched set - see `filterSource` below.
	const hasFullSet = tagsSource === 'client' && Array.isArray(fullBrandMessages);

	// "RCS" and "Coupons" are derived per message, the campaign tags are the
	// ones set in the admin panel. The tag filter (client or server) runs
	// first, then sort, then the paywall - in that order - so the
	// paywall behaves identically whether the filter ran on the server or
	// in memory.
	const effectiveMessages = withEffectiveTags(messages);
	// Chip list source: the full set once it's ready (so chips cover the
	// brand's entire history), else whatever's currently loaded. This is
	// deliberately independent of whether a tag is selected - chips must be
	// available before the user picks one.
	const chipSource = hasFullSet ? withEffectiveTags(fullBrandMessages) : effectiveMessages;
	const availableTags =
		tagsSource === 'server' ? serverTags : extractUniqueTags(chipSource);
	// Message-list source: the full set is only ever substituted in while a
	// tag is actively selected client-side (`isClientFiltering`). With no tag
	// selected, this is always `effectiveMessages` (i.e. `messages`), so the
	// default "All" view, its count, and its pagination/Load More behaviour
	// are byte-for-byte the same as before the full-set fetch existed -
	// `filterByTag(source, '')` alone is NOT enough to guarantee that, since
	// it returns whatever `source` it's given unchanged.
	const filterSource = isClientFiltering && hasFullSet
		? withEffectiveTags(fullBrandMessages)
		: effectiveMessages;
	const taggedMessages = isFiltering
		? withEffectiveTags(filteredMessages)
		: filterByTag(filterSource, selectedTag);
	const sortedMessages = sortMessagesByDate(taggedMessages, sortDirection);
	const { visible: visibleMessages } = applyPaywall(sortedMessages);
	// With no tag selected, `isClientFiltering` is false, so this falls
	// straight through to `pagination`, exactly as before this feature
	// existed. Only while client-filtering over an already-complete full set
	// do we report no next page, since there is nothing further to fetch for
	// the active filter and the unrelated "load more unfiltered messages"
	// pagination must not leak through as a dead "Load More" click.
	const activePagination = isFiltering
		? filteredPagination
		: isClientFiltering && hasFullSet
			? null
			: pagination;
	const paywalled = shouldShowPaywall({
		user,
		isPro,
		count: visibleMessages.length,
		hasNextPage: Boolean(activePagination?.nextPage),
		// While client-side tag filtering drops activePagination, fall back to the
		// last server pagination so the card still shows when older messages exist.
		hasOlder: (activePagination || pagination)?.hasOlder === true,
	});

	useEffect(() => {
		const fetchBuilderData = async () => {
			try {
				const res = await fetch(
					`https://cdn.builder.io/api/v2/content/brand-page?apiKey=0de1f0bc4e504a15a1cd1b48dbd897e2&query[data.brandId]=${params?.brandId}`
				);
				const data = await res.json();

				if (data?.results?.length > 0) {
					const filteredResults = data.results.filter((item) =>
						item.data.title.includes(params?.brandId)
					);
					if (filteredResults.length > 0) {
						setContent(filteredResults);
					}
				} else {
					console.warn('No content found for this brand ID.');
				}
			} catch (error) {
				console.error('Error fetching Builder data:', error);
			}
		};

		if (params?.brandId) {
			fetchBuilderData();
		}
	}, [params?.brandId]);

	// Reads the (module-cache-backed, see `getFullBrandMessageSet` above) full
	// message set for `brandId` and applies it to state - guarded so a stale
	// result is discarded if the brand changed again while this was in
	// flight, including while awaiting a fetch shared with another mount via
	// the cache. Only called when the available-tags endpoint is unavailable
	// (see the effect below) - the server path never needs this. Fails open:
	// any error leaves `fullBrandMessages` as `null`, which downstream
	// (`hasFullSet`) is treated identically to "never attempted", i.e.
	// chips/filtering keep working from whatever messages are already loaded
	// on screen.
	const applyFullBrandMessageSet = async (brandId, myRequestId) => {
		setFullBrandMessagesLoading(true);
		try {
			const { messages: collected, truncated } = await getFullBrandMessageSet(brandId, authKeyRef.current);
			if (fullSetRequestIdRef.current !== myRequestId) return; // stale (brand changed again)
			setFullBrandMessages(collected);
			setFullSetTruncated(truncated);
		} catch (error) {
			if (fullSetRequestIdRef.current !== myRequestId) return; // stale
			console.error('Error fetching full brand message set for tag filtering:', error);
			// Fail open: leave `fullBrandMessages` as `null` so chip/filter
			// derivation falls back to today's loaded-messages-only behaviour.
			setFullBrandMessages(null);
			setFullSetTruncated(false);
		} finally {
			if (fullSetRequestIdRef.current === myRequestId) {
				setFullBrandMessagesLoading(false);
			}
		}
	};

	// Landing on a different brand starts unfiltered, so a filter chosen on
	// one brand can never silently hide another brand's messages. It also
	// resets the tag chip source back to 'client' and re-fetches the
	// server's available-tags list for the new brand.
	useEffect(() => {
		const myRequestId = ++tagsRequestIdRef.current;
		const myFullSetRequestId = ++fullSetRequestIdRef.current;
		setSelectedTag('');
		setTagsSource('client');
		setServerTags([]);
		setFilteredMessages([]);
		setFilteredPagination(null);
		setFullBrandMessages(null);
		setFullBrandMessagesLoading(false);
		setFullSetTruncated(false);

		if (!params?.brandId) return;

		(async () => {
			const tags = await fetchAvailableTags({ brandId: params.brandId });
			if (tagsRequestIdRef.current !== myRequestId) return; // stale (brand changed again)
			if (Array.isArray(tags)) {
				setServerTags(tags);
				setTagsSource('server');
				return; // server already covers the whole brand; no fallback fetch needed
			}
			// On failure (`tags === null`) we deliberately stay in 'client'
			// mode; see the resilience comment on `tagsSource` above. Kick off
			// the (module-cached) background full-set fetch so chips/filtering
			// can still cover the whole brand rather than just whatever's
			// loaded on screen.
			applyFullBrandMessageSet(params.brandId, myFullSetRequestId);
		})();
	}, [params?.brandId]);

	const itemsPerPage = 10;

	// Bumped on every tier change so a slower response from the previous tier
	// can't overwrite the refreshed list.
	const tierEpochRef = useRef(0);

	const getBrandMessages = async (currentPage = 1, replace = false) => {
		const myEpoch = tierEpochRef.current;
		setLoading(true);
		try {
			const response = await fetch(
				`https://api.tinytexts.co/api/brands/messages/${params?.brandId}?page=${
					currentPage - 1
				}&size=${itemsPerPage}`,
				{ headers: await getAuthHeaders() }
			);

			if (!response.ok) {
				throw new Error(`Error: ${response.status} - ${response.statusText}`);
			}

			const data = await response.json();
			if (tierEpochRef.current !== myEpoch) return; // stale (tier changed)
			setMessages((prevMessages) =>
				replace ? data?.messages || [] : [...prevMessages, ...data?.messages]
			);
			setPagination(data?.pagination || null);
		} catch (error) {
			if (tierEpochRef.current !== myEpoch) return; // stale (tier changed)
			console.error('Error fetching brand messages:', error);
		} finally {
			if (tierEpochRef.current === myEpoch) setLoading(false);
		}
	};

	// Fetches a page of messages carrying `tag` directly from the server
	// (across the brand's whole message set), replacing (page 0) or appending
	// to (subsequent pages) the filtered list.
	const getFilteredBrandMessages = async (tag, pageNumber = 0) => {
		const myRequestId = ++filterRequestIdRef.current;
		setFilteredLoading(true);
		try {
			const response = await fetch(
				`https://api.tinytexts.co/api/brands/messages/${params?.brandId}?page=${pageNumber}&size=${itemsPerPage}&tag=${encodeURIComponent(
					tag
				)}`,
				{ headers: await getAuthHeaders() }
			);
			if (!response.ok) {
				throw new Error(`Error: ${response.status} - ${response.statusText}`);
			}
			const data = await response.json();
			if (filterRequestIdRef.current !== myRequestId) return; // stale response
			setFilteredMessages((prev) =>
				pageNumber === 0 ? data?.messages || [] : [...prev, ...(data?.messages || [])]
			);
			setFilteredPagination(data?.pagination || null);
			setFilteredLoading(false);
		} catch (error) {
			if (filterRequestIdRef.current !== myRequestId) return; // stale response
			console.error('Error fetching tag-filtered brand messages:', error);
			// Resilience fallback: revert to filtering the messages already
			// loaded on the client, exactly as this page behaved before
			// server-side tag filtering existed, rather than showing an error
			// or a stuck loading state.
			setTagsSource('client');
			setFilteredMessages([]);
			setFilteredPagination(null);
			setFilteredLoading(false);
		}
	};

	const getSingleBrand = async () => {
		if (brand) return;
		setLoading(true);
		try {
			const response = await fetch(
				`https://api.tinytexts.co/api/brands/${params?.brandId}`,
				{ headers: await getAuthHeaders() }
			);
			if (!response.ok) {
				throw new Error(`Error: ${response.status} - ${response.statusText}`);
			}
			const { brand: rawBrand } = await response.json();
			setBrand(transformBrand(rawBrand));
		} catch (error) {
			console.error('Error fetching brand details:', error);
		} finally {
			setLoading(false);
		}
	};

	const loadMoreBrands = () => {
		if (pagination?.nextPage) {
			setPage((prevPage) => prevPage + 1);
		}
	};

	const handleLoadMore = () => {
		if (isFiltering) {
			if (filteredPagination?.nextPage != null) {
				getFilteredBrandMessages(selectedTag, filteredPagination.nextPage);
			}
			return;
		}
		loadMoreBrands();
	};

	// When the selected tag changes (and the server is known to support tag
	// filtering), fetch page 0 of the filtered results and replace the
	// filtered list. Clearing the tag just stops using `filteredMessages` -
	// no fetch is needed since `messages` already holds the unfiltered list.
	useEffect(() => {
		if (!selectedTag || tagsSource !== 'server') {
			setFilteredMessages([]);
			setFilteredPagination(null);
			setFilteredLoading(false);
			return;
		}
		getFilteredBrandMessages(selectedTag, 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedTag, tagsSource, params?.brandId]);

	// The server-rendered first page is always the logged-out tier. When the
	// signed-in user (or PRO status) differs from that, reload page 1 with
	// the user's token so they get the right tier.
	const loadedAuthKeyRef = useRef(':free');
	useEffect(() => {
		if (loadedAuthKeyRef.current === authKey || !params?.brandId) return;
		loadedAuthKeyRef.current = authKey;
		tierEpochRef.current += 1;
		setPage(1);
		getBrandMessages(1, true);
		if (selectedTag && tagsSource === 'server') {
			getFilteredBrandMessages(selectedTag, 0);
		}
		if (tagsSource === 'client' && fullBrandMessages != null) {
			applyFullBrandMessageSet(params.brandId, ++fullSetRequestIdRef.current);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authKey]);

	useEffect(() => {
		if (page <= 1) {
			return;
		}
		getBrandMessages(page);
	}, [page]);

	useEffect(() => {
		getSingleBrand();
	}, []);

	return (
		<>
			{/* <Loader renderChildren={messages?.length > 0 && brand}> */}

			<div className='flex w-full'>
				<LeftSidebar />
				<div className='flex-1 min-w-0 overflow-x-hidden px-4 md:px-8 mx-auto mb-4'>
				{brand?.brandId && (
					<div className='flex flex-col items-center justify-center gap-2 mb-6 mt-8'>
						<h1 className='text-4xl font-bold text-black text-center mb-2'>
							{brand.name} Text Messages
						</h1>
						{brand?.websiteLink && (
							<p className='mb-0 text-center'>
								<Link
									href={getVigLinkUrl(brand.websiteLink)}
									passHref
									data-viglink='true'
									target='_blank'
									className='text-black hover:underline'
								>
									{brand.websiteLink}
								</Link>
							</p>
						)}
						{brand?.description && (
							<p className='text-lg font-normal text-center text-gray-700'>
								{brand.description}
							</p>
						)}
						<FollowButton brandId={brand.brandId} />
					</div>
				)}
				<div className='flex flex-wrap items-center gap-4 mb-3'>
					<SortControl value={sortDirection} onChange={setSortDirection} />
					<TagFilter
						tags={availableTags}
						selected={selectedTag}
						onSelect={setSelectedTag}
						labels={FILTER_TAG_LABELS}
						order={FILTER_TAG_ORDER}
						isPro={isPro}
					/>
				</div>
				{isClientFiltering && hasFullSet && fullSetTruncated && (
					<p className='mb-3 text-sm text-gray-500'>
						Showing matches from this brand&apos;s most recent{' '}
						{FULL_SET_MAX_MESSAGES.toLocaleString()} messages.
					</p>
				)}
				{isFiltering && filteredLoading && filteredMessages.length === 0 ? (
					<p className='py-10 text-center text-gray-600'>
						Loading messages for this filter…
					</p>
				) : isFiltering && sortedMessages.length === 0 ? (
					<p className='py-10 text-center text-gray-600'>
						No messages match this filter.
					</p>
				) : isClientFiltering && sortedMessages.length === 0 ? (
					<p className='py-10 text-center text-gray-600'>
						{hasFullSet
							? 'No messages match this filter.'
							: 'No messages loaded yet match this filter.'}
					</p>
				) : isMd ? (
					<MessagesSlider
						messages={visibleMessages}
						paywalled={paywalled}
						fetchMoreMessages={async () => {
							if (!paywalled && activePagination?.nextPage) {
								handleLoadMore();
								return [];
							}
							return [];
						}}
					/>
				) : (
					<div className='grid gap-4 md:grid-cols-3 lg:grid-cols-4'>
						{visibleMessages?.map((message) => (
							<MessageCard
								key={message?.messageId}
								message={message}
							/>
						))}
					</div>
				)}
				{paywalled && !isMd && <PaywallCard />}
				{!paywalled && activePagination?.nextPage && (
					<div className='justify-center hidden mt-6 md:flex'>
						<button
							className={`px-4 py-2 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-300 ${
								!activePagination?.nextPage
									? 'bg-gray-400 cursor-not-allowed'
									: 'bg-blue-700 hover:bg-blue-600'
							}`}
							onClick={handleLoadMore}
							aria-label='Load more brands'
							disabled={!activePagination?.nextPage}
						>
							{(isFiltering ? filteredLoading : loading) ? 'Loading...' : 'Load More'}
						</button>
					</div>
				)}
				{content === null && (
					<div className={styles.builderContent}>
						<BuilderComponent
							model='brand-page'
							content={content}
						/>
					</div>
				)}
				{content?.length > 0 && (
					<div className={styles.builderContent}>
						<BuilderComponent
							model='brand-page'
							content={content[0]}
						/>
					</div>
				)}
				</div>
			</div>

			{/* </Loader> */}
			<SecondaryFooter />
		</>
	);
};

export default SingleBrandPage;
